import { Component } from '@theme/component';

/**
 * Generate a random string
 * @param {number} length - Length of the random string
 * @param {string} chars - Characters to use for generating the string
 * @returns {string} - Random string
 */
function randomString(length, chars) {
  let result = '';
  const values = new Uint32Array(length);
  crypto.getRandomValues(values);
  for (let i = 0; i < length; i++) {
    result += chars.charAt(values[i] % chars.length);
  }
  return result;
}

/**
 * Validate phone number
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - Whether the phone number is valid
 */
function validatePhone(phone) {
  // Basic phone validation - adjust regex as needed
  const phoneRegex = /^[\d\s\-\+\(\)]+$/;
  return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 10;
}

/**
 * Validate email
 * @param {string} email - Email to validate
 * @returns {boolean} - Whether the email is valid
 */
function validateEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Upload file to server
 * @param {File} file - File to upload
 * @returns {Promise<string>} - URL of uploaded file
 */
async function uploadFile(file) {
  if (!file) return '';

  const fileNameWithoutExt = file.name.split('.').slice(0, -1).join('.');
  const fileType = file.type;
  const fileName = `prescription-${fileNameWithoutExt}`;
  
  const url = `https://app.asmaraku.com/api/upload?name=${encodeURIComponent(fileName)}&type=${encodeURIComponent(fileType)}&dir=apotek&shop=asmaraku-com`;
  
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await fetch(url, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    const data = await response.json();
    // Assuming the API returns the file URL in a 'url' or 'file' property
    return data.url || data.file || data.path || '';
  } catch (error) {
    console.error('File upload error:', error);
    throw new Error('Gagal mengupload file. Silakan coba lagi.');
  }
}

/**
 * Submit prescription data
 * @param {Object} data - Form data
 * @returns {Promise<Object>} - Response data
 */
async function submitPrescription(data) {
  const url = 'https://app.asmaraku.com/api/psef/upload/create/new';
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error(`Submit failed: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Submit error:', error);
    throw new Error('Gagal mengirim data. Silakan coba lagi.');
  }
}

/**
 * Send email notification
 * @param {Object} data - Email data
 * @returns {Promise<Object|null>} - Response data or null
 */
async function sendEmail(data) {
  const url = 'https://app.asmaraku.com/api/psef/email/send';
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error(`Email send failed: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Email send error:', error);
    // Don't throw error for email - form was already submitted successfully
    console.warn('Email notification failed, but form was submitted successfully');
    return null;
  }
}

/**
 * Show error message for a field
 * @param {HTMLElement} container - Form container
 * @param {string} fieldName - Field name
 * @param {string} message - Error message
 */
function showError(container, fieldName, message) {
  const errorElement = container.querySelector(`[data-error="${fieldName}"]`);
  const inputElement = container.querySelector(`[name="${fieldName}"]`);
  
  if (errorElement) {
    errorElement.textContent = message;
    errorElement.setAttribute('data-visible', 'true');
  }
  
  if (inputElement) {
    inputElement.classList.add('error');
  }
}

/**
 * Clear error message for a field
 * @param {HTMLElement} container - Form container
 * @param {string} fieldName - Field name
 */
function clearError(container, fieldName) {
  const errorElement = container.querySelector(`[data-error="${fieldName}"]`);
  const inputElement = container.querySelector(`[name="${fieldName}"]`);
  
  if (errorElement) {
    errorElement.textContent = '';
    errorElement.setAttribute('data-visible', 'false');
  }
  
  if (inputElement) {
    inputElement.classList.remove('error');
  }
}

/**
 * Clear all error messages
 * @param {HTMLElement} container - Form container
 */
function clearAllErrors(container) {
  const errorElements = container.querySelectorAll('.prescription-form__error');
  const inputElements = container.querySelectorAll('.prescription-form__input');
  
  errorElements.forEach(el => {
    el.textContent = '';
    el.setAttribute('data-visible', 'false');
  });
  
  inputElements.forEach(el => {
    el.classList.remove('error');
  });
}

/**
 * Validate form data
 * @param {FormData} formData - Form data
 * @param {HTMLElement} container - Form container
 * @returns {boolean} - Whether the form is valid
 */
function validateForm(formData, container) {
  let isValid = true;
  
  clearAllErrors(container);
  
  // Validate name
  const name = /** @type {string} */ (formData.get('name'));
  if (!name || name.trim() === '') {
    showError(container, 'name', 'Nama wajib diisi');
    isValid = false;
  }
  
  // Validate email
  const email = /** @type {string} */ (formData.get('email'));
  if (!email || email.trim() === '') {
    showError(container, 'email', 'Email wajib diisi');
    isValid = false;
  } else if (!validateEmail(email)) {
    showError(container, 'email', 'Email tidak valid');
    isValid = false;
  }
  
  // Validate phone
  const phone = /** @type {string} */ (formData.get('phone'));
  if (!phone || phone.trim() === '') {
    showError(container, 'phone', 'Nomor telepon wajib diisi');
    isValid = false;
  } else if (!validatePhone(phone)) {
    showError(container, 'phone', 'Nomor telepon tidak valid');
    isValid = false;
  }
  
  // Validate location
  const location = /** @type {string} */ (formData.get('location'));
  if (!location || location === '') {
    showError(container, 'location', 'Lokasi wajib dipilih');
    isValid = false;
  }
  
  // Validate address
  const address = /** @type {string} */ (formData.get('address'));
  if (!address || address.trim() === '') {
    showError(container, 'address', 'Alamat wajib diisi');
    isValid = false;
  }
  
  // Validate complaint
  const complaint = /** @type {string} */ (formData.get('complaint'));
  if (!complaint || complaint.trim() === '') {
    showError(container, 'complaint', 'Keluhan wajib diisi');
    isValid = false;
  }
  
  return isValid;
}

class PrescriptionFormComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    
    /** @type {HTMLFormElement | null} */
    this.form = /** @type {HTMLFormElement | null} */ (this.querySelector('[ref="prescriptionForm"]'));
    
    /** @type {HTMLButtonElement | null} */
    this.submitButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="submitButton"]'));
    
    /** @type {HTMLElement | null} */
    this.submitText = this.querySelector('[ref="submitText"]');
    
    /** @type {HTMLElement | null} */
    this.loadingSpinner = this.querySelector('[ref="loadingSpinner"]');
    
    /** @type {HTMLElement | null} */
    this.successMessage = this.querySelector('[ref="successMessage"]');
    
    /** @type {HTMLElement | null} */
    this.successText = this.querySelector('[ref="successText"]');
    
    /** @type {HTMLInputElement | null} */
    this.fileInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[ref="fileInput"]'));
    
    /** @type {HTMLElement | null} */
    this.fileStatus = this.querySelector('[ref="fileStatus"]');
    
    this.isSubmitting = false;

    // Spam guard: a hidden field only a bot would fill, plus a minimum time
    // since page load — real visitors need a few seconds to fill the form,
    // bots submit near-instantly.
    this.formLoadedAt = Date.now();

    this.setupEventListeners();
  }

  /**
   * @returns {boolean}
   */
  looksLikeBot() {
    const hp = /** @type {HTMLInputElement | null} */ (this.querySelector('[name="hp_field"]'));
    const MIN_FILL_MS = 3000;
    return Boolean(hp && hp.value) || (Date.now() - this.formLoadedAt) < MIN_FILL_MS;
  }
  
  setupEventListeners() {
    if (!this.form) return;
    
    // Form submit handler
    this.form.addEventListener('submit', async (/** @type {Event} */ e) => {
      e.preventDefault();
      await this.handleSubmit();
    });
    
    // File input change handler
    if (this.fileInput && this.fileStatus) {
      this.fileInput.addEventListener('change', (/** @type {Event} */ e) => {
        const target = /** @type {HTMLInputElement} */ (e.target);
        const file = target.files?.[0];
        if (file && this.fileStatus) {
          this.fileStatus.textContent = `File terpilih: ${file.name}`;
        } else if (this.fileStatus) {
          this.fileStatus.textContent = '';
        }
      });
    }
    
    // Clear error on input
    const inputs = this.form.querySelectorAll('.prescription-form__input');
    inputs.forEach((/** @type {Element} */ input) => {
      const inputElement = /** @type {HTMLInputElement} */ (input);
      inputElement.addEventListener('input', () => {
        if (inputElement.name) {
          clearError(this, inputElement.name);
        }
      });
    });
  }
  
  async handleSubmit() {
    if (this.isSubmitting || !this.form) return;

    if (this.looksLikeBot()) {
      // Silently drop the submission without hitting the API — no fetch,
      // no upload, no notification. No visible reaction either, so the bot
      // has no signal to adapt against.
      return;
    }

    const formData = new FormData(this.form);
    
    // Validate form
    if (!validateForm(formData, this)) {
      return;
    }
    
    this.isSubmitting = true;
    this.setLoadingState(true);
    
    try {
      // Step 1: Upload file if present
      let fileUrl = '';
      const file = this.fileInput?.files?.[0];
      if (file && this.fileStatus) {
        this.fileStatus.textContent = 'Mengupload file...';
        fileUrl = await uploadFile(file);
        this.fileStatus.textContent = 'File berhasil diupload';
      }
      
      // Step 2: Prepare and submit prescription data
      const name = /** @type {string} */ (formData.get('name') || '');
      const email = /** @type {string} */ (formData.get('email') || '');
      const phone = /** @type {string} */ (formData.get('phone') || '');
      const location = /** @type {string} */ (formData.get('location') || '');
      const address = /** @type {string} */ (formData.get('address') || '');
      const complaint = /** @type {string} */ (formData.get('complaint') || '');
      const no_prescription = /** @type {string} */ (formData.get('no_prescription') || '');
      
      const now = new Date();
      const prescriptionData = {
        id: randomString(6, 'aAbCdDeEfFgGhHiIjJkKlLmMnNoOpPqQrRsStTuUvVwWxXyYzZ'),
        date: now.toISOString(),
        name: name,
        email: email,
        phone: phone,
        location: location,
        address: address,
        complaint: complaint,
        no_prescription: no_prescription,
        file: fileUrl,
        status: ''
      };
      
      await submitPrescription(prescriptionData);
      
      // Step 3: Send email notification
      const emailData = {
        name: name,
        email: email,
        phone: phone,
        address: address,
        complaint: complaint,
        no_prescription: no_prescription,
        file: fileUrl
      };
      
      await sendEmail(emailData);
      
      // Step 4: Show success message
      this.showSuccess(email);
      
    } catch (error) {
      console.error('Form submission error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Terjadi kesalahan. Silakan coba lagi.';
      alert(errorMessage);
      this.setLoadingState(false);
      this.isSubmitting = false;
    }
  }
  
  /**
   * Set loading state
   * @param {boolean} isLoading - Whether the form is loading
   */
  setLoadingState(isLoading) {
    if (!this.submitButton || !this.submitText || !this.loadingSpinner) return;
    
    if (isLoading) {
      this.submitButton.disabled = true;
      this.submitText.style.display = 'none';
      this.loadingSpinner.style.display = 'inline-flex';
    } else {
      this.submitButton.disabled = false;
      this.submitText.style.display = 'inline';
      this.loadingSpinner.style.display = 'none';
    }
  }
  
  /**
   * Show success message
   * @param {string} email - User's email
   */
  showSuccess(email) {
    if (!this.form || !this.successMessage || !this.successText) return;
    
    // Hide form
    this.form.style.display = 'none';
    
    // Show success message.
    // Build via DOM (not innerHTML) so the user-supplied email can never be
    // interpreted as HTML/script.
    this.successText.textContent = `Terima kasih, pesanan resep Anda sudah diterima. Kami akan segera menghubungi Anda melalui email ${email}.`;
    const uploadAgainLink = document.createElement('a');
    uploadAgainLink.href = '/pages/upload-resep';
    uploadAgainLink.textContent = 'Upload resep kembali';
    this.successText.append(document.createElement('br'), uploadAgainLink);
    this.successMessage.style.display = 'block';
    
    // Reset loading state
    this.setLoadingState(false);
    this.isSubmitting = false;
    
    // Scroll to success message
    this.successMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

customElements.define('prescription-form-component', PrescriptionFormComponent);
